import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, Lightbulb, AlertTriangle, X } from "lucide-react";
import { Section } from "@/mockups/dc";
import { RuleChips } from "@/components/campaigns/Builders";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/campaigns`;
const EXAMPLES = ["Multi-dog homes who boarded last Christmas but haven't booked this year", "Daycare regulars with 10+ visits who have never boarded", "Boarders in Chalfont or 18914 who haven't visited in 6 months"];

const Ideas = ({ ideas, onPick }) => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }} data-testid="ai-suggestions">
    {ideas.map((s, i) => (
      <button key={i} className="card" style={{ textAlign: "left", padding: 14, cursor: "pointer" }} onClick={() => onPick(s.audience)} data-testid={`ai-suggestion-${i}`}>
        <b style={{ fontSize: 14 }}>{s.title}</b>
        <div style={{ fontSize: 12, margin: "4px 0 6px" }}>{s.audience}</div>
        <div className="muted" style={{ fontSize: 11 }}>{s.why}</div>
      </button>
    ))}
  </div>
);

const Draft = ({ d, setD, onBuild, onReset, busy }) => {
  const email = d.channel === "email";
  return (
    <div className="raised" style={{ borderRadius: 12, padding: 16, display: "flex", flexDirection: "column", gap: 10 }} data-testid="ai-draft">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input className="input" style={{ flex: 1, fontWeight: 700 }} value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} data-testid="ai-draft-name" />
        <button className="btn btn-sm btn-ghost" onClick={onReset} aria-label="Start over" data-testid="ai-draft-reset"><X size={14} /></button>
      </div>
      {d.explanation && <div className="muted" style={{ fontSize: 12, fontStyle: "italic" }} data-testid="ai-draft-explanation">{d.explanation}</div>}
      <span className="label">Rules (tap a chip to edit)</span>
      <RuleChips rules={d.rules} labels={null} onChange={(rules) => setD({ ...d, rules })} busy={busy} />
      {d.unsupported?.length > 0 && (
        <div className="alert" style={{ fontSize: 12, alignItems: "flex-start" }} data-testid="ai-draft-unsupported">
          <AlertTriangle size={14} style={{ flexShrink: 0 }} /><span>I can't filter on this yet, so it's not in the rules: <b>{d.unsupported.join("; ")}</b></span>
        </div>
      )}
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span className="label" style={{ marginRight: 4 }}>Send by</span>
        {[["sms", "Text"], ["email", "Email"]].map(([k, l]) => <button key={k} className={`btn btn-sm ${d.channel === k ? "btn-primary" : ""}`} onClick={() => setD({ ...d, channel: k })} data-testid={`ai-draft-channel-${k}`}>{l}</button>)}
      </div>
      {email ? (
        <>
          <input className="input" value={d.subject_template} onChange={(e) => setD({ ...d, subject_template: e.target.value })} data-testid="ai-draft-subject" />
          <textarea className="input" rows={8} value={d.email_template} onChange={(e) => setD({ ...d, email_template: e.target.value })} data-testid="ai-draft-email" />
        </>
      ) : <textarea className="input" rows={4} value={d.sms_template} onChange={(e) => setD({ ...d, sms_template: e.target.value })} data-testid="ai-draft-sms" />}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span className="muted" style={{ fontSize: 11 }}>Next you'll see exactly who matches. Nothing is sent until you tap Send.</span>
        <button className="btn btn-primary" style={{ marginLeft: "auto" }} disabled={busy || !d.rules.length} onClick={onBuild} data-testid="ai-draft-build">{busy && <Loader2 size={13} className="animate-spin" />}Build campaign</button>
      </div>
    </div>
  );
};

export const AiBuilder = () => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [draft, setDraft] = useState(null);
  const nav = useNavigate();
  const call = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(e.response?.data?.detail || "AI couldn't answer, please try again"); } setBusy(null); };
  const parse = (t = text) => call("parse", async () => { const { data } = await axios.post(`${API}/ai/parse`, { text: t }); setDraft(data); });
  const suggest = () => call("suggest", async () => { const { data } = await axios.post(`${API}/ai/suggest`, { text }); setIdeas(data.suggestions); });
  const build = () => call("build", async () => {
    const { rules, name, channel, sms_template, subject_template, email_template, source_text } = draft;
    const { data } = await axios.post(API, { rules, name, channel, sms_template, subject_template, email_template, source_text });
    nav(`/campaigns/${data.id}`);
  });
  const pick = (t) => { setText(t); setIdeas([]); parse(t); };
  return (
    <Section title="Describe your audience" sub="Type who you want to reach in plain English. AI turns it into rules you can check before anything is built." icon={Sparkles} testid="ai-builder">
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <textarea className="input" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder={`e.g. ${EXAMPLES[0]}`} data-testid="ai-audience-input"
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && text.trim()) parse(); }} />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <button className="btn btn-primary" disabled={!!busy || text.trim().length < 5} onClick={() => parse()} data-testid="ai-parse-btn">{busy === "parse" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}Turn into rules</button>
          <button className="btn" disabled={!!busy} onClick={suggest} title="Uses what you typed as the focus, if anything" data-testid="ai-suggest-btn">{busy === "suggest" ? <Loader2 size={13} className="animate-spin" /> : <Lightbulb size={13} />}Suggest ideas</button>
          {!text && EXAMPLES.slice(1).map((x, i) => <button key={x} className="chip" style={{ cursor: "pointer", fontSize: 11 }} onClick={() => setText(x)} data-testid={`ai-example-${i}`}>{x}</button>)}
        </div>
        {ideas.length > 0 && <Ideas ideas={ideas} onPick={pick} />}
        {draft && <Draft d={draft} setD={setDraft} onBuild={build} onReset={() => setDraft(null)} busy={!!busy} />}
      </div>
    </Section>
  );
};
